import { useRef, useState, type PointerEvent } from 'react';
import { displayObject, poseOf, type DisplayObject } from '../../animation/timeline';
import * as A from '../../domain/animationOps';
import { POSE_ANGLE_KEYS, POSE_ANGLE_LIMIT, POSE_OFFSET_KEYS, POSE_OFFSET_LIMIT, type Animation, type Character, type CharacterPose, type PoseKey, type SceneObject } from '../../domain/schemas';
import type { AnimationSnapshot } from '../../animation/animationController';
import type { Observable } from '../../lib/store';
import { useObservable, useObservableSelector } from '../../lib/useObservable';
import { applySegment, characterHandles, characterRig, characterSvg, segmentTransforms, type Segment } from '../../scene/characters';
import { useServices } from '../../state/services';
import { Button, Panel } from '../ui';
import type { TransformEditFn } from './useAnimationEditing';

const LABELS: Record<PoseKey, string> = {
  head: 'Head',
  torso: 'Torso',
  armL: 'Left arm',
  armR: 'Right arm',
  legL: 'Left leg',
  legR: 'Right leg',
  headX: 'Head X',
  headY: 'Head Y',
  torsoX: 'Torso X',
  torsoY: 'Torso Y',
};
const VIEW_W = 100;
const VIEW_H = 200;
const HANDLE_ORDER: Segment[] = ['torso', 'legL', 'legR', 'armL', 'armR', 'head'];
const deg = (rad: number) => (rad * 180) / Math.PI;
const wrap = (d: number) => ((((d + 180) % 360) + 360) % 360) - 180;

/**
 * Pose editor for a character instance (Phase 3B): numeric sliders for every pose channel
 * plus draggable handles on a preview (drag a hand, foot, the head or the neck to rotate
 * that segment around its joint). With Auto-Key on, edits key the selected animation at
 * the playhead; otherwise they change the instance's base pose. The character design
 * (shared by every instance) is never changed here.
 */
export function PoseInspector({ object, character, animation, frames, locked, onTransform }: { object: SceneObject; character: Character; animation: Animation | null; frames: Observable<AnimationSnapshot>; locked: boolean; onTransform: TransformEditFn }) {
  const { animation: bus } = useServices();
  const autoKey = useObservable(bus.autoKey) && !!animation;
  // Follow the displayed frame only while Auto-Key shows (and keys) values at the playhead.
  const frame = useObservableSelector(frames, (v) => (autoKey ? v.frame : 0));
  const svgRef = useRef<SVGSVGElement>(null);
  const [dragging, setDragging] = useState<{ segment: Segment; pointerId: number; angle: number } | null>(null);
  const shown: DisplayObject = autoKey && animation ? displayObject(object, A.valuesAt(animation, object.id, frame)) : object;
  const base = shown.pose ?? poseOf(object);
  const key = (segment: Segment): PoseKey => segment;
  const pose: CharacterPose = dragging ? { ...base, [key(dragging.segment)]: dragging.angle } : base;
  const set = (label: string, patch: Partial<CharacterPose>, coalesce?: string) =>
    onTransform(label, [object.id], () => ({ pose: patch }), coalesce && `${object.id}:pose:${coalesce}${autoKey ? `@${frame}` : ''}`, 'base');

  // Handle geometry in the 100 × 200 preview (the same transforms the renderer uses).
  const transforms = segmentTransforms(characterRig(character), pose, VIEW_W, VIEW_H);
  const handles = characterHandles(character);
  const px = (p: { x: number; y: number }) => ({ x: p.x * VIEW_W, y: p.y * VIEW_H });
  const flip = object.type === 'character' && object.flipX;
  const view = (p: { x: number; y: number }) => (flip ? { x: VIEW_W - p.x, y: p.y } : p);
  const points = HANDLE_ORDER.map((segment) => {
    const t = transforms[segment];
    return { segment, joint: view({ x: t.x, y: t.y }), end: view(applySegment(t, px(handles[segment].end))) };
  });

  const toView = (e: PointerEvent) => {
    const r = svgRef.current!.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * VIEW_W, y: ((e.clientY - r.top) / r.height) * VIEW_H };
  };
  /** Angle of a segment that points from its joint towards `p` (relative to rest and parent). */
  const angleFor = (segment: Segment, p: { x: number; y: number }) => {
    const h = handles[segment];
    const t = transforms[segment];
    const joint = view({ x: t.x, y: t.y });
    const rest = { x: px(h.end).x - px(h.joint).x, y: px(h.end).y - px(h.joint).y };
    const restAngle = Math.atan2(rest.y, flip ? -rest.x : rest.x);
    let a = deg(Math.atan2(p.y - joint.y, p.x - joint.x) - restAngle);
    if (flip) a = -a;
    // Head and arms are attached to the torso: their angle is relative to it.
    if (segment === 'head' || segment === 'armL' || segment === 'armR') a -= base.torso;
    return Math.round(Math.max(-POSE_ANGLE_LIMIT, Math.min(POSE_ANGLE_LIMIT, wrap(a))));
  };

  const onDown = (segment: Segment) => (e: PointerEvent<SVGCircleElement>) => {
    if (locked) return;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    setDragging({ segment, pointerId: e.pointerId, angle: base[key(segment)] });
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!dragging || dragging.pointerId !== e.pointerId) return;
    setDragging({ ...dragging, angle: angleFor(dragging.segment, toView(e)) });
  };
  const onUp = (e: PointerEvent<SVGSVGElement>) => {
    if (!dragging || dragging.pointerId !== e.pointerId) return;
    const d = dragging;
    setDragging(null);
    // One drag = one change = one undo step.
    if (d.angle !== base[key(d.segment)]) set('Pose', { [key(d.segment)]: d.angle });
  };

  const slider = (k: PoseKey, offset: boolean) => {
    const limit = offset ? POSE_OFFSET_LIMIT : POSE_ANGLE_LIMIT;
    const id = `pose-${k}`;
    const value = pose[k];
    return (
      <div key={k} className="flex flex-col gap-0.5">
        <label htmlFor={id} className="flex justify-between text-[11px] text-zinc-400">
          <span>{LABELS[k]}</span>
          <span className="font-mono text-zinc-300">{offset ? `${Math.round(value * 1000) / 10}%` : `${Math.round(value)}°`}</span>
        </label>
        <input
          id={id}
          type="range"
          min={-limit}
          max={limit}
          step={offset ? 0.005 : 1}
          value={value}
          disabled={locked}
          onChange={(e) => set('Pose', { [k]: Number(e.target.value) }, k)}
          data-testid={id}
        />
      </div>
    );
  };

  return (
    <Panel title={`Pose · ${object.name}`} headingLevel={3}>
      <div className="flex flex-col gap-3" data-testid="pose-inspector">
        {locked && <p className="text-xs text-amber-300">This instance is locked. Unlock it to pose it.</p>}
        <p className="text-xs text-zinc-400">
          {autoKey ? (
            <>
              <span className="font-semibold text-red-300">Auto-Key</span> · pose changes key “{animation!.name}” at frame {frame}.
            </>
          ) : (
            <>Base pose of this instance{animation ? ' (turn on Auto-Key to key poses)' : ''}. Other instances of “{character.name}” are not affected.</>
          )}
        </p>
        <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
          <div className="relative mx-auto h-[200px] w-[100px] shrink-0 rounded-md bg-zinc-950">
            <img alt="" aria-hidden="true" width={VIEW_W} height={VIEW_H} src={`data:image/svg+xml;utf8,${encodeURIComponent(characterSvg(character, flip, pose))}`} className="absolute inset-0" />
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
              className="absolute inset-0 h-full w-full touch-none"
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={() => setDragging(null)}
              aria-label="Pose handles: drag a dot to rotate that body part (or use the sliders)"
              role="img"
            >
              {points.map(({ segment, joint, end }) => (
                <g key={segment}>
                  <line x1={joint.x} y1={joint.y} x2={end.x} y2={end.y} stroke="#38bdf8" strokeWidth={0.8} strokeDasharray="2 2" pointerEvents="none" />
                  <circle cx={end.x} cy={end.y} r={5} fill={dragging?.segment === segment ? '#38bdf8' : '#ffffff'} stroke="#0284c7" strokeWidth={1.2} className="cursor-grab" onPointerDown={onDown(segment)} data-testid={`pose-handle-${segment}`}>
                    <title>{LABELS[segment]}</title>
                  </circle>
                </g>
              ))}
            </svg>
          </div>
          <div className="flex flex-col gap-2">
            <fieldset className="grid grid-cols-2 gap-x-3 gap-y-1">
              <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Rotation</legend>
              {POSE_ANGLE_KEYS.map((k) => slider(k, false))}
            </fieldset>
            <fieldset className="grid grid-cols-2 gap-x-3 gap-y-1">
              <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Offsets</legend>
              {POSE_OFFSET_KEYS.map((k) => slider(k, true))}
            </fieldset>
            <Button size="sm" disabled={locked} onClick={() => set('Reset pose', { head: 0, torso: 0, armL: 0, armR: 0, legL: 0, legR: 0, headX: 0, headY: 0, torsoX: 0, torsoY: 0 })}>
              Reset pose
            </Button>
          </div>
        </div>
      </div>
    </Panel>
  );
}
